import React from 'react';
import moment from 'moment';
import IconInfoCircle from '@src/assets/icons/icon-info-circle.svg';
import LockRoundedIcon from '@src/assets/images/lock-rounded.svg';
import route from '@src/constants/route';
import Typography from '@src/new-components/Typography';
import CustomButton from '@src/new-components/Button';
import { useHistory } from 'react-router-dom';
import { delimitNumber } from '@src/utils/number';
import pluralize from '@src/utils/pluralize';
import { CODE_TASK, TASK_REWARD_CODE } from '@src/constants/referral';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { useTranslation } from 'react-i18next';
import useCountdownTimer from '@src/hooks/useCountdownTimer';
import Tooltip from '@src/components/Tooltip';
import actions from '@src/redux/actions';
import { useDispatch, useSelector } from 'react-redux';
import { COLOR } from '@src/styles/color';
import {
  StyledTask,
  StyledTaskButton,
  StyledTaskContent,
  StyledTaskCredits,
  StyledTaskInfo,
} from './index.style';

const Task = ({ task, hasUncompletedReferralTask, taskCompleted }) => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const referralConfig = useSelector((state) => state.referralConfig);
  const { charsPerPoint } = referralConfig || {};
  const registeredAt = moment(user?.createdAt);
  const now = moment();
  const diffHours = now.diff(registeredAt, 'hours');

  const after24h = registeredAt.add(24, 'hours');
  const countdown = useCountdownTimer(after24h.toISOString());

  const showCountdown =
    diffHours < 24 &&
    task?.code === CODE_TASK.REFERRAL_CODE_APPLIED_SUCCESSFULLY &&
    !taskCompleted;

  const rewardCredits = (() => {
    if (task?.code === CODE_TASK.REFERRAL_CODE_APPLIED_SUCCESSFULLY) {
      return (
        task?.rewards
          ?.filter(
            ({ details, type }) =>
              details?.target === TASK_REWARD_CODE.REFEREE && type === 'CREDIT',
          )
          ?.reduce((sum, { details }) => sum + (details?.amount || 0), 0) || 0
      );
    }
    return (
      task?.rewards
        ?.filter((item) => item?.details?.target === TASK_REWARD_CODE.REFEREE)
        ?.reduce(
          (sum, { details }) =>
            sum + (details?.amount || 0) * (charsPerPoint || 0),
          0,
        ) || 0
    );
  })();

  const rewardNote = task?.rewards?.find((item) => item?.note);

  const formatWithZero = (num) => (num < 10 ? `0${num}` : num);

  const renderCountdown = () =>
    `${formatWithZero(countdown.hours)}:${formatWithZero(
      countdown.minutes,
    )}:${formatWithZero(countdown.seconds)}`;

  const renderButtonAction = () => {
    if (taskCompleted) {
      return (
        <CustomButton
          variant="contained"
          textProps={{
            color: COLOR.darkBlue,
            variantText: 'body-14-medium',
          }}
          customStyle={{
            borderRadius: '8px',
            height: '32px',
            minWidth: '120px',
            padding: '4px 16px',
          }}
          disabled
        >
          {t('isCompleted')}
        </CustomButton>
      );
    }
    if (
      hasUncompletedReferralTask &&
      task?.code === CODE_TASK.REFEREE_FIRST_PAID_ORDER &&
      diffHours < 24
    ) {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            width: '105px',
            justifyContent: 'flex-end',
          }}
        >
          <img src={LockRoundedIcon} alt="lock-rounded" />
          <Typography
            style={{
              textAlign: 'center',
            }}
            variant="body-14-medium"
            color={COLOR.colorGrayV2}
          >
            {t('unlockAfterRedeeming')}
          </Typography>
        </div>
      );
    }
    if (task?.code === CODE_TASK.REFERRAL_CODE_APPLIED_SUCCESSFULLY) {
      return (
        <CustomButton
          variant="contained"
          textProps={{
            color: COLOR.darkBlue,
            variantText: 'body-14-medium',
          }}
          customStyle={{
            borderRadius: '8px',
            height: '32px',
            minWidth: '120px',
            padding: '4px 16px',
          }}
          onClick={() =>
            dispatch(actions.user.updateIsOpeningRedeemReferralDialog(true))
          }
        >
          {t('redeemCode')}
        </CustomButton>
      );
    }

    if (
      task?.code === CODE_TASK.REFEREE_FIRST_PAID_ORDER &&
      !hasUncompletedReferralTask
    ) {
      return (
        <CustomButton
          variant="contained"
          textProps={{
            color: COLOR.darkBlue,
            variantText: 'body-14-medium',
          }}
          customStyle={{
            borderRadius: '8px',
            height: '32px',
            minWidth: '120px',
            padding: '4px 16px',
          }}
          onClick={() => history.push(route.PAYMENT)}
        >
          {t('buyPackage')}
        </CustomButton>
      );
    }

    return <div style={{ width: '120px' }} />;
  };

  return (
    <StyledTask>
      <img
        style={{
          width: 24,
          height: 24,
        }}
        src={task?.icon}
        alt="task-icon"
      />

      <StyledTaskContent>
        <StyledTaskInfo>
          <Typography
            className="info-title"
            variant="body-16-medium"
            color={COLOR.black.default}
          >
            {task?.title?.[language]}

            {showCountdown && (
              <Typography
                className="countdown-text-desktop"
                variant="body-14-medium"
                color={COLOR.textRejected}
                style={{ marginLeft: '20px' }}
              >
                {renderCountdown()}
              </Typography>
            )}
          </Typography>
          <Typography
            className="info-text"
            variant="body-14-regular"
            color={COLOR.descriptionUpload}
          >
            {task?.description?.[language]}
          </Typography>
        </StyledTaskInfo>
        <StyledTaskCredits>
          <Typography
            style={{ textAlign: 'center', width: 'max-content' }}
            variant="body-16-medium"
            color={COLOR.descriptionUpload}
            className="reward-credits-text"
          >
            <Typography variant="body-16-bold" color={COLOR.descriptionGray}>
              +{delimitNumber(rewardCredits, language)}
            </Typography>{' '}
            {pluralize(rewardCredits, t('credit'), language)}
          </Typography>
        </StyledTaskCredits>
      </StyledTaskContent>
      <div className="tootlip-info-container">
        {rewardNote ? (
          <Tooltip
            arrow
            title={rewardNote?.note?.[language]}
            placement="top"
            maxWidth="100%"
          >
            <img
              style={{
                width: 14,
                height: 14,
                marginLeft: '-10px',
                marginTop: '4px',
              }}
              src={IconInfoCircle}
              alt="icon-info-circle"
            />
          </Tooltip>
        ) : (
          <InfoRoundedIcon
            sx={{ fontSize: 14, color: 'transparent', marginLeft: '-10px' }}
          />
        )}
      </div>
      <StyledTaskButton>
        {showCountdown && (
          <Typography
            className="countdown-text-mobile"
            variant="body-14-medium"
            color={COLOR.textRejected}
          >
            {renderCountdown()}
          </Typography>
        )}

        {renderButtonAction()}
      </StyledTaskButton>
    </StyledTask>
  );
};

export default Task;
